import { ReactNode, useState } from 'react';
import styled, { css } from 'styled-components';
import { TextInput } from './TextInput';
import { Input } from './Input';
import { OutsideClicker, useFormConfigurationContext, useKeyDown } from '../../../..';

const ScrollListBoxContainer = styled.div`
  position: relative;
  z-index: 1;
`;

const ScrollListBox = styled.div`${({ theme }) => `
  position: absolute;
  overflow-y: auto;
  border:none;
  max-height:30vh;
  margin-top:${theme.padding[2]};
  border: ${theme.borderWidth[1]} solid ${theme.colours.neutral[6]};
  border-radius: ${theme.borderRadius[1]};
  background: ${theme.colours.neutral[10]};
  width:100%;
`}`;

const ScrollListBoxItem = styled.div<{isCurrent: boolean, focused: boolean, allowItemSelect?: boolean}>`${({
  theme, isCurrent, focused, allowItemSelect = true,
}) => `
  padding:${theme.padding[4]};
  padding-top:${theme.padding[1]};
  padding-bottom:${theme.padding[1]};
  font-size:${theme.fontSize[1]};

  ${allowItemSelect ? `
    cursor: pointer;
    :hover{
      background: ${theme.colours.secondary[6]};
      font-weight: ${theme.fontWeight.normal};
    }
    ${isCurrent ? `
        background: ${theme.colours.secondary[4]};
    ` : ''}

    ${focused ? `
      background: ${theme.colours.secondary[5]};
      font-weight: ${theme.fontWeight.bold};
    ` : ''}
  ` : ''}
`}`;

const ScrollListSearchContainerStyles = css`
  padding-bottom:0;
`.toString();

type ListBoxProps = {
  id: string,
  searchValue: string,
  label?: string,
  searchPlaceholder?: string,
  errors?: string[],
  options: { [key: string]: string },
  labelStyles?: string,
  disabled?: boolean,
  onChangeSearch: (newValue: string) => void,
  onChangeItem: (newValue: string) => void,
  required?: boolean,
  prefix?: ReactNode,
  suffix?: ReactNode,
  allowItemSelect?: boolean
}

export function SearchListBox({
  id,
  label,
  searchPlaceholder = 'Search',
  searchValue,
  errors = [],
  onChangeItem,
  onChangeSearch,
  options,
  labelStyles,
  disabled,
  required,
  prefix,
  suffix,
  allowItemSelect = true,
}: ListBoxProps) {
  const [listVisible, setListVisible] = useState<boolean>(false);

  const { isDisabled } = useFormConfigurationContext(disabled);

  const showList = () => { setListVisible(true); };
  const hideList = () => { setListVisible(false); };
  const toggleList = () => {
    if (listVisible) {
      hideList();
    } else {
      showList();
    }
  };

  const onClickItem = (itemKey: string) => {
    if (allowItemSelect) {
      onChangeItem(itemKey ?? '');
      hideList();
    }
  };

  const optionsArray = Object.entries(options);

  const listShown = (listVisible && !isDisabled && optionsArray.length > 0);

  const [focusedItemIndex, setFocusedItemIndex] = useState<number>(-1);

  useKeyDown(() => {
    if (listShown) {
      setFocusedItemIndex((prev) => Math.min(prev + 1, optionsArray.length - 1));
    }
  }, ['ArrowDown'], listShown);

  useKeyDown(() => {
    if (listShown) {
      setFocusedItemIndex((prev) => Math.max(prev - 1, 0));
    }
  }, ['ArrowUp'], listShown);

  useKeyDown(() => {
    if (listShown && focusedItemIndex >= 0) {
      onClickItem(optionsArray[focusedItemIndex][0]);
    }
  }, ['Enter'], listShown);

  useKeyDown(() => {
    if (listShown) {
      hideList();
    }
  }, ['Tab'], false);

  return (
    <OutsideClicker onClickOutside={hideList}>
      <Input
        id={id}
        label={label}
        errors={errors}
        labelStyles={labelStyles}
        required={required}
      >
        <TextInput
          id={`${id}-search`}
          placeholder={searchPlaceholder}
          labelStyles={labelStyles}
          value={searchValue}
          onChange={(newValue: string) => onChangeSearch(newValue)}
          disabled={isDisabled}
          containerStyles={ScrollListSearchContainerStyles}
          onFocus={showList}
          errors={errors}
          prefix={prefix}
          suffix={<div onClick={toggleList}>{suffix || <i className={`mi-chevron-${listShown ? 'up' : 'down'}`} />}</div>}
        />
      </Input>

      {listShown && (
        <ScrollListBoxContainer>
          <ScrollListBox id={`${id}-list`}>
            {
            optionsArray.map(([optionKey, optionValue], index) => (
              <ScrollListBoxItem
                key={optionKey}
                onClick={() => onClickItem(optionKey)}
                isCurrent={optionKey === searchValue}
                focused={focusedItemIndex === index}
                allowItemSelect={allowItemSelect}
              >
                {optionValue}
              </ScrollListBoxItem>
            ))
          }
          </ScrollListBox>
        </ScrollListBoxContainer>
      )}
    </OutsideClicker>
  );
}
